import React, { useEffect, useRef, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Button,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import Controls from "../../atoms/Controls";
import useStore from "../../helpers/useStore";
import { useMasterTables, useMasterTablesFacade } from "../../stores/MasterTables";
import { toJS } from "mobx";
import { AppointmentContext } from "../../stores/Appointment/appointment.provider";
import {
  AddressDto,
  AddressDtoTypeEnum,
  AppointmentDto,
  AppointmentTemplateDto,
  PatientAvailabilityDto,
  RxDto,
  TherapyDto,
  TherapyRxDto,
  TimeSlotDto,
} from "../../api-client";
import styled from "styled-components";
import TextArea from "../../atoms/TextArea";
import { mobiliTheme } from "../../themes/mobiliTheme";
import FlatCard from "../../atoms/FlatCard";
import finalPropsSelectorFactory from "react-redux/es/connect/selectorFactory";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { visitTypeToIcon } from "../../helpers/visitTypeMapping";
import { visitTypeReverseMapping } from "../../helpers/parseInput";
import { UserContext } from "../../stores/User/User.provider";
import { useCurrentLocation } from "../../stores/User/user.queries";
import { Label } from "@mui/icons-material";
import notificationStore from "../../stores/Notification/notificationStore";
import { observer } from "mobx-react";
import { useRxLackingAppointments } from "../../stores/Rx/rx.queries";
import { usePatient } from "../../stores/Patients/patient.queries";
import { useCreateForTreatments } from "../../stores/Appointment/appointment.mutation";
import { remainingAppointmentCount } from "../../helpers/remainingAppointments";
import TherapyRxMethodCell from "./TherapyRxMethodCell";

/**
 * Visit fees on an appointment template (MD-232).
 *
 * The fee used to sit on each prescription line as `TherapyRxDto.visitFee`, so a
 * field only the template flow read was carried by every consumer of a line. It
 * now lives on the template as a `therapyRx.id -> therapy.id` map, matching how
 * the unscheduled-appointment path already passes fees. These accessors keep the
 * call sites as readable as the old property access was.
 */
type WithVisitFees = { visitFees?: Record<string, string> };

const lineVisitFee = (t: WithVisitFees, lineId?: string): string | undefined =>
  lineId ? t.visitFees?.[lineId] : undefined;

const setLineVisitFee = (
  t: WithVisitFees,
  lineId: string | undefined,
  feeId: string | null,
): void => {
  if (!lineId) return;
  if (!t.visitFees) t.visitFees = {};
  if (feeId) t.visitFees[lineId] = feeId;
  else delete t.visitFees[lineId];
};

const clearAllVisitFees = (t: WithVisitFees): void => {
  t.visitFees = {};
};

const hasAnyVisitFee = (t: WithVisitFees): boolean =>
  Object.values(t.visitFees ?? {}).some(Boolean);


// import './table.css';

interface NewRxProps {
  patientId: number;

}

interface SelectedVisitFeeObj {
  therapyRxId: string;
  visitFee: string | null;
}

const StyledTableContainer = styled(TableContainer)`
  overflow-x: auto !important;
  max-width: 100% !important;
`;

const StyledTable = styled(Table)`
  table-layout: auto !important;
`;

const StyledTableCell = styled(TableCell)`
  padding: 2px 4px !important;
  height: 10px !important;
`;

const StyledTableRow = styled(TableRow)`
  height: 10px !important;
`;
const StyledTextField = styled(TextArea)`
  width: 100% !important;
  height: 20px !important;
`;

const StyledButtonContainer = styled(Grid)`
  margin-top: 1px !important;
`;

// Define more styled components as needed...

const NewRx: React.FC<NewRxProps> = observer(({
  patientId,
}) => {

  const {data: masterData} = useMasterTables();
  const {mutate:createForTreatments} = useCreateForTreatments();

const {data:patientData} = usePatient(patientId);
const {data:rxnoAppointments, isLoading} = useRxLackingAppointments(patientId!);
  const [selectedTherapies, setSelectedTherapies] = useState<any[]>([]); // Type annotation added
  const [selectedPerpetualTherapies, setSelectedPerpetualTherapies] = useState<any[]>([]); // Type annotation added
  const [showCrossMark, setShowCrossMark] = useState<boolean>(false); // Track whether to show the cross mark
  const initialFrequency: any = rxnoAppointments?.[0]?.therapyRxs?.[0]?.frequency || "";
  const initialperpetualFrequency: any =
    rxnoAppointments?.filter((rx) => rx.isPerpetual)?.[0]?.therapyRxs?.[0]?.frequency || "";

  const notPerpetualRxs = rxnoAppointments?.filter((rx) => !rx.isPerpetual) || [];
  // MD-348: `amount`/`scheduledCount` on the line are prescribed UNITS, but
  // AppointmentTemplateDto.amount (what this seeds) is a count of
  // APPOINTMENTS — one units-per-day slot each. Sending units straight
  // through doubled every Doppelbehandlung line (unitsPerDay 2).
  const initialAmount = remainingAppointmentCount(
    notPerpetualRxs?.[0]?.therapyRxs?.[0]?.amount,
    notPerpetualRxs?.[0]?.therapyRxs?.[0]?.scheduledCount,
    notPerpetualRxs?.[0]?.therapyRxs?.[0]?.unitsPerDay,
  );

  // console.log(
    // "initialAmount",
    // initialAmount,
    // "scheduled ",
    // rxnoAppointments?.[0]?.therapyRxs?.[0]?.scheduledCount,
    // rxnoAppointments?.[0]?.therapyRxs?.[0]?.rx?.rxNumber
  // );

  const UserStore = useStore(UserContext);
  const { user } = UserStore;
  const currentLocation = useCurrentLocation();
  // console.debug("New Rx Rendered, currentlocation: ", currentLocation, currentLocation?.address);

  const [amount, setAmount] = useState<number>(initialAmount);
  const [frequency, setFrequency] = useState(initialFrequency);
  // const [perpetualAmount, setperpetualAmount] = useState<number>(-1);
  const [perpetualfrequency, setperpetualFrequency] = useState(initialperpetualFrequency);
  const [isSuggestionDialogOpen, setsuggestionDialogOpen] = useState(false);
  const [addresses, setAddresses] = useState<AddressDto[]>([]);
  const [addressId, setAddressId] = useState<string>();
  const [perpetualAddressId, setPerpetualAddressId] = useState<string>();
  const [selectedVisitFeeObj, setSelectedVisitFeeObj] = useState<SelectedVisitFeeObj | null>();
  const [selectedPerpetualVisitFeeObj, setPerpetualSelectedVisitFeeObj] =
    useState<SelectedVisitFeeObj | null>();
  const [visitFeeValueToFeeCodeMap, setVisitFeeValueToFeeCodeMap] = useState<{
    [key: string]: string | null;
  }>();
  /**
   * The form's DRAFT, which is not yet a valid request: no frequency is chosen
   * until the user picks one, and `AppointmentTemplateDto` rightly requires it
   * (the server rejects a template without one). Typing the draft as the request
   * DTO forced a `frequency: {}` placeholder that satisfied the compiler while
   * being exactly the empty object the server refuses.
   */
  type AppointmentTemplateDraft = Omit<AppointmentTemplateDto, "frequency"> & {
    frequency?: AppointmentTemplateDto["frequency"];
  };

  const [newData, setNewData] = useState<AppointmentTemplateDraft>({
    therapyRx: [],
    amount: 0,
    addressId: "",
  });
  const [perpetualData, setPerpetualData] = useState<AppointmentTemplateDraft>({
    therapyRx: [],
    addressId: "",
  });


  const { getTherapyId } = useMasterTablesFacade();
  // console.log("RXNOAPPA", rxnoAppointments, initialAmount, initialFrequency);
  const [suggestionData, setSuggestionData] = useState<any>(null); // Initialize with null or appropriate initial data

  //Not Perpetual
  let prevAddressIdRef = useRef<string | undefined>();
  let prevVisitFeeObjRef = useRef<Object | undefined | null>();
  let prevSelectedTherapies = useRef<any[]>();
  //Perpetual
  let prevPerpetualAddressIdRef = useRef<string | undefined>();
  let prevPerpetualVisitFeeObjRef = useRef<Object | undefined | null>();
  let prevPerpetualSelectedTherapies = useRef<any[]>();

  useEffect(() => {
    // console.log("rxnoAppointments:", rxnoAppointments); // Debug log to check rxnoAppointments
    // Initialize selectedTherapies with all therapies
    const notPerpetualTherapies = rxnoAppointments?.flatMap((rx) => {
      return rx.isPerpetual ? [] : rx.therapyRxs;
    });
    const PerpetualTherapies = rxnoAppointments?.flatMap((rx) => {
      return rx.isPerpetual ? rx.therapyRxs : [];
    });
    setSelectedTherapies(notPerpetualTherapies!);
    setSelectedPerpetualTherapies(PerpetualTherapies!);
    // setperpetualAmount(-1)
    setperpetualFrequency(initialperpetualFrequency?.text);
    setAmount(initialAmount);
    setFrequency(initialFrequency?.text);
  }, [rxnoAppointments]);

  useEffect(() => {
    if (!masterData) return;
    
    const frequencyObject = toJS(masterData)
      .filter((obj) => "therapyFrequency" in obj)
      .map((obj) => obj.therapyFrequency) // Access the therapyFrequency array
      .flat() // Flatten the array if needed
      .find((obj) => obj.text === frequency);
    const perpetualfrequencyObject = toJS(masterData)
      .filter((obj) => "therapyFrequency" in obj)
      .map((obj) => obj.therapyFrequency) // Access the therapyFrequency array
      .flat() // Flatten the array if needed
      .find((obj) => obj.text === perpetualfrequency);

    let newData: AppointmentTemplateDraft = {
      therapyRx: selectedTherapies,
      amount: amount,
      frequency: frequencyObject ? frequencyObject : frequency,
      addressId: addressId,
    };

    let perpetualData: AppointmentTemplateDraft = {
      therapyRx: selectedPerpetualTherapies,
      frequency: perpetualfrequencyObject ? perpetualfrequencyObject : perpetualfrequency,
      addressId: perpetualAddressId,
    };

    // Initial Address assignment
    if (!newData.addressId) {
      const newAddressId = getAddressForAppointmentTemplate(newData, notPerpetualRxs);
      newData.addressId = newAddressId;
      setAddressId(newData.addressId);
    }

    if (!perpetualData.addressId) {
      const newAddressId = getAddressForAppointmentTemplate(perpetualData, perpetaulRx);
      perpetualData.addressId = newAddressId;
      setPerpetualAddressId(newAddressId);
    }

    //Initial VisitFee assignment
    if (!selectedVisitFeeObj) {
      newData = assignVisitFeeToAppointmentTemplate(newData, notPerpetualRxs, false,selectedTherapies);
    }

    if (!selectedPerpetualVisitFeeObj) {
      perpetualData = assignVisitFeeToAppointmentTemplate(perpetualData, perpetaulRx, true,selectedPerpetualTherapies);
    }

    //Not Perpetual ;
    if (
      prevVisitFeeObjRef.current !== undefined &&
      (selectedVisitFeeObj !== prevVisitFeeObjRef.current || selectedVisitFeeObj === null)
    ) {
      newData = onAfterVisitFeeChange(selectedVisitFeeObj!, newData, notPerpetualRxs, false,selectedTherapies);
    }

    if (
      (prevAddressIdRef.current !== undefined && addressId !== prevAddressIdRef.current) ||
      (prevSelectedTherapies.current !== undefined &&
        selectedTherapies !== prevSelectedTherapies.current)
    ) {
      newData = onAfterAddressChange(addressId!, newData, notPerpetualRxs!, false,selectedTherapies);
    }
    //Perpetual

    if (
      (prevPerpetualAddressIdRef.current !== undefined &&
        perpetualAddressId !== prevPerpetualAddressIdRef.current) ||
      (prevPerpetualSelectedTherapies.current !== undefined &&
        selectedPerpetualTherapies !== prevPerpetualSelectedTherapies.current)
    ) {
      perpetualData = onAfterAddressChange(perpetualAddressId!, perpetualData, perpetaulRx, true,selectedPerpetualTherapies);
    }

    if (
      prevPerpetualVisitFeeObjRef.current !== undefined &&
      (selectedPerpetualVisitFeeObj !== prevPerpetualVisitFeeObjRef.current ||
        selectedPerpetualVisitFeeObj === null)
    ) {
      perpetualData = onAfterVisitFeeChange(
        selectedPerpetualVisitFeeObj!,
        perpetualData,
        perpetaulRx,
        true,
        selectedPerpetualTherapies
      );
    }

    setNewData(newData);
    setPerpetualData(perpetualData);

    prevAddressIdRef.current = addressId;
    prevVisitFeeObjRef.current = selectedVisitFeeObj;
    prevSelectedTherapies.current = selectedTherapies;

    prevPerpetualAddressIdRef.current = perpetualAddressId;
    prevPerpetualVisitFeeObjRef.current = selectedPerpetualVisitFeeObj;
    prevPerpetualSelectedTherapies.current = selectedPerpetualTherapies;
  }, [
    amount,
    frequency,
    selectedTherapies,
    selectedPerpetualTherapies,
    perpetualfrequency,
    addressId,
    perpetualAddressId,
    selectedVisitFeeObj,
    selectedPerpetualVisitFeeObj,
    // Both load asynchronously. Without them here the effect bails on a missing
    // masterData, or derives no address, and never runs again — so after a page
    // refresh the template stayed empty even with rows ticked.
    masterData,
    addresses,
  ]);

  const doesPraxisTherapyRxSelected = (templateData: AppointmentTemplateDraft,selectedTherapyData:TherapyRxDto[]) => {
    let selected = false;

    for (let i = 0; i < templateData?.therapyRx?.length; i++) {
      if (!getDefaultVisitFeeByRxOrt(templateData.therapyRx[i].id!, rxnoAppointments || [],selectedTherapyData)) {
        selected = true;
        break;
      }
    }
    return selected;
  };
  const onAfterAddressChange = (
    addressId: string,
    templateData: AppointmentTemplateDraft,
    rxs:RxDto[],
    isContinuous: boolean,
    selectedTherapyData: TherapyRxDto[]
  ): AppointmentTemplateDraft => {
    let _newData = { ...templateData };

    if (addressId) {
      if (isSelectedAddressIsPraxis(addressId)) {
        //If address is praxis, making Visit Fee null and in selected therapies Haus/ Heim and Praxis are allowed
        clearAllVisitFees(_newData);
        // setSelectedVisitFeeObj(null);
      } else {
        //If selected therapies has only selected praxis therapies then patient address cant be selected then assign praxis address

        // let allTherapyRxIsPraxis = _newData.therapyRx.length && true;
        // let praxisTherapyIds: string[] = [];
        // _newData.therapyRx.forEach((thrxObj) => {
        //   //If default visit fee value is null then it means the Ort is Praxis

        //   if (getDefaultVisitFeeByRxOrt(thrxObj.id!, rxs)) {
        //     allTherapyRxIsPraxis = false;
        //   } else {
        //     praxisTherapyIds.push(thrxObj.id!);
        //   }
        // });

        if (doesPraxisTherapyRxSelected(_newData,selectedTherapyData)) {
          // assign praxis address
          let prevAddressRef = isContinuous ? prevPerpetualAddressIdRef : prevAddressIdRef;
          if (
            !isSelectedAddressIsPraxis(addressId) &&
            prevAddressRef.current &&
            addressId !== prevAddressRef.current
          ) {
          
            notificationStore.showMessage(
              "Für Praxis-Rezepte ist nur die Praxis-Adresse möglich.",
              "error"
            );
          }

          const locationAddress = addresses?.filter(
            (addressObj) =>
              !addressObj.isVisit && !addressObj.isPrimary && !addressObj.isAlternativeBilling
          )[0];
          let setAddressFunc = isContinuous ? setPerpetualAddressId : setAddressId;
          // if (locationAddress) setAddressId(locationAddress.id);
          setAddressFunc(locationAddress?.id);

          // setSelectedTherapies([]);
        } else {
          //

          //assigning visitFee
          _newData = assignVisitFeeToAppointmentTemplate(_newData, rxs, isContinuous,selectedTherapyData);
        }
      }
      _newData.addressId = addressId;
    }
    return _newData;
  };
  const onAfterVisitFeeChange = (
    visitFeeObj: SelectedVisitFeeObj,
    templateData: AppointmentTemplateDraft,
    rxs: RxDto[],
    isContinuous: boolean,
    selectedTherapyData:TherapyRxDto[]
  ): AppointmentTemplateDraft => {
    let _newData = templateData;
    // console.log("AddressId change visitfee", addressId);
    if (visitFeeObj) {
      if (isSelectedAddressIsPraxis((isContinuous ? perpetualAddressId : addressId)!)) {
        const patientAddress = addresses?.filter(
          (addressObj) => addressObj.isVisit || addressObj.isPrimary
        )[0];

        //if Praxis therapRxObj is selected then we cannot change address to patient's address instead clear visit fee
        if (doesPraxisTherapyRxSelected(templateData,selectedTherapyData)) {
          const setVisitFeeFunc = isContinuous
            ? setPerpetualSelectedVisitFeeObj
            : setSelectedVisitFeeObj;
          clearAllVisitFees(_newData);
          setVisitFeeFunc(null);
          notificationStore.showMessage("Hausbesuchsgebühr nicht erlaubt bei Praxis-Adresse", "error");
        } else {
          let setAddressFunc = isContinuous ? setPerpetualAddressId : setAddressId;
          setAddressFunc(patientAddress?.id ? patientAddress.id : "");
        }
      } else {
        _newData = assignVisitFeeToAppointmentTemplate(_newData, rxs, isContinuous,selectedTherapyData);
      }
    }

    return _newData;
  };

  //Setting patient addresses in addresses state array
  // Fetch patient addresses
  useEffect(() => {
    if (patientId) {

          setAddresses((prevAddresses: any) => {
            // Get the patient's addresses and filter based on addressLine1 and zip
            const filteredNewAddresses = (patientData?.addresses || []).filter(
              (address) => address.contactInfo?.addressLine1 && address.contactInfo.zip
            );

            // Get IDs of the filtered new addresses
            const newAddressIds = filteredNewAddresses.map((addr) => addr.id);

            // Filter out old addresses that have the same IDs as the new filtered addresses
            const filteredPrevAddresses = prevAddresses?.filter(
              (addr: any) => !newAddressIds.includes(addr.id)
            );

            // Combine filtered previous addresses and new filtered addresses
            return [...filteredPrevAddresses, ...filteredNewAddresses];
          });
      
    
    }
  }, [patientData]);

  // Set user address in addresses state arrray
  useEffect(() => {
    if (user && currentLocation?.address) {
      let address = currentLocation.address;
      
      if (address.contactInfo?.addressLine1 && address.contactInfo.zip) {
        setAddresses((prevAddresses) => {
          const newAddress = address ?? null;
  
          if (newAddress) {
            // Remove any existing address of type "praxis"
            // const updatedAddresses = prevAddresses.filter((addr) => addr.id !== newAddress.id);
            const updatedAddresses = prevAddresses.filter((addr) => addr.type !== AddressDtoTypeEnum.Praxis);
  
            return [...updatedAddresses, newAddress]; // Add the new "praxis" address
          } else {
            return prevAddresses.filter((addr) => addr !== null);
          }
        });
      }
    }
    // currentLocation arrives from its own query, usually after user on a fresh page
    // load — without it here the Praxis address never made it into the options.
  }, [user, currentLocation]);
  

  // useEffect(() => {
  //   fetchAndSetAddress();
  // }, [patientId, user, patientDataRefreshFlag]);

  // console.log("usesrtg", addresses, user);

  const handleRowSelect = (therapyId: any) => {
    // console.log("Selected therapyId", selectedTherapies, checkedMap);
    const selectedRx: any = rxnoAppointments?.find((rx) =>
      rx?.therapyRxs?.some((therapy: any) => therapy.id === therapyId)
    );
    const selectedTherapy: any = selectedRx?.therapyRxs?.find((therapy: any) => therapy.id === therapyId);
    // The tarif group sits on the prescription; `therapyRx.therapy` was removed in MD-232.
    const tarifGroupId = selectedRx?.tarifGroup?.id ?? selectedRx?.tarifGroupId;

    // console.log("THERAPY WHEN IT WAS X-ED", checkedMap[selectedTherapy.id]);
    if (selectedTherapy) {
      if (!checkedMap[selectedTherapy.id]) {
        selectedTherapy.visitFee = null;
      } else {
        selectedTherapy.visitFee = getTherapyId(
          tarifGroupId,
          checkedMap[selectedTherapy.id] as any
        );
      }
    }
    // console.log("selectedTherapy", selectedTherapy);
    if (selectedTherapy) {
      setSelectedTherapies([...selectedTherapies, selectedTherapy]);
    }
  };

  const handleperpetualRowSelect = (therapyId: any) => {
    const selectedRx: any = rxnoAppointments?.find((rx) =>
      rx?.therapyRxs?.some((therapy: any) => therapy.id === therapyId)
    );
    const selectedTherapy: any = selectedRx?.therapyRxs?.find((therapy: any) => therapy.id === therapyId);
    // The tarif group sits on the prescription; `therapyRx.therapy` was removed in MD-232.
    const tarifGroupId = selectedRx?.tarifGroup?.id ?? selectedRx?.tarifGroupId;
    // console.log("selectedTherapy", selectedTherapy);

    if (selectedTherapy) {
      if (!checkedMapPerpetual[selectedTherapy.id]) {
        selectedTherapy.visitFee = null;
      } else {
        selectedTherapy.visitFee = getTherapyId(
          tarifGroupId,
          checkedMapPerpetual[selectedTherapy.id] as any
        );
      }
    }
    if (selectedTherapy) {
      setSelectedPerpetualTherapies([...selectedPerpetualTherapies, selectedTherapy]);
    }
  };
  const handleperpetualRemove = (therapyId: any) => {
    const updatedRxs = rxnoAppointments?.map((rx) => {
      const updatedTherapies = rx?.therapyRxs?.filter((therapy: any) => therapy.id !== therapyId);
      if (selectedPerpetualVisitFeeObj?.therapyRxId == therapyId)
        resetSelectedVisitFeeObj(therapyId, true);

      return { ...rx, therapies: updatedTherapies };
    });

    // setRxs(updatedRxs);
    const updatedSelectedTherapies = selectedPerpetualTherapies.filter(
      (selected) => selected.id !== therapyId
    );
    setSelectedPerpetualTherapies(updatedSelectedTherapies);

    setPerpetualData((prevData) => ({
      ...prevData,
      therapyRx: updatedSelectedTherapies,
    }));
  };

  const handleRemove = (therapyId: any) => {
    // const updatedRxs = rxs.map((rx) => {
    //   const updatedTherapies = rx?.therapyRxs?.filter((therapy: any) => therapy.id !== therapyId);
    //   return { ...rx, therapies: updatedTherapies };
    // });

    // // setRxs(updatedRxs);
    // setSelectedTherapies(selectedTherapies.filter((selected) => selected.id !== therapyId));

    const updatedSelectedTherapies = selectedTherapies.filter(
      (selected) => selected.id !== therapyId
    );

    setSelectedTherapies(updatedSelectedTherapies);

    // Update newData to reflect the changes in selectedTherapies
    setNewData((prevData) => ({
      ...prevData,
      therapyRx: updatedSelectedTherapies,
    }));

    if (selectedVisitFeeObj?.therapyRxId == therapyId) {
      resetSelectedVisitFeeObj(therapyId, false);
    }
  };

  // const handleEdit = (therapyId: any, field: string, newValue: any) => {
  //   // Update the therapy in the state
  //   const updatedRxs = rxs.map((rx) => {
  //     const updatedTherapies = rx.therapyRxs?.map((therapy: any) => {
  //       if (therapy.id === therapyId) {
  //         return { ...therapy, [field]: newValue };
  //       }
  //       return therapy;
  //     });
  //     return { ...rx, therapies: updatedTherapies };
  //   });

  //   setRxs(updatedRxs);
  // };
  const normalizeVisitType = (visitType: string | undefined) => {
    if (visitType === visitTypeReverseMapping["hb"] || visitType == visitTypeReverseMapping["hm"]) {
      return visitTypeReverseMapping["hb"] + visitTypeReverseMapping["hm"];
    }
    return visitType;
  };


  const getAddressOfRxByTherapyRx = (thearpyRxId: string, rxs: RxDto[]): AddressDto | null => {
    let address = null;
    const rxIndex = rxs.findIndex((rxObj) =>
      rxObj.therapyRxs?.some((thxobj) => thxobj.id == thearpyRxId)
    );
    if (rxIndex > -1) {
      let rxVisitType = rxs[rxIndex].isHomeVisit;
      //Hiem or Haus
      if (
        rxVisitType == true
       
      ) {
        //address with isVisit flag to true
        const addressWithVisitSet = addresses?.filter((addressObj) => addressObj.isVisit)[0];
        if (addressWithVisitSet?.id) {
          // templateData.addressId = addressWithVisitSet.id;
          address = addressWithVisitSet;
        } else {
          // we choose isPrimary otherwise null
          const addressWithPrimarySet = addresses?.filter((addressObj) => addressObj.isPrimary)[0];
          if (addressWithPrimarySet) {
            address = addressWithPrimarySet;
          } else {
            // console.log("Address of patient is not present");
          }
        }
      } // Praxis
      else if (rxVisitType == false) {
        //address with isPrimary to true
        const locationAddress = addresses?.filter(
          (addressObj) =>
            !addressObj.isPrimary && !addressObj.isVisit && !addressObj.isAlternativeBilling
        )[0];
        if (locationAddress?.id) {
          //console.log("Praxis Address", primaryAddress);

          // templateData.addressId = primaryAddress.id;
          address = locationAddress;
        } else {
          // console.log("Praxis location address is not present");
        }
      }
    }

    return address;
  };

  const isSelectedAddressIsPraxis = (addressId: string) => {
    let isSelectedAddressPraxisAddress = false;
    const locationAddresses = addresses?.filter(
      (addressObj) =>
        !addressObj.isPrimary && !addressObj.isVisit && !addressObj.isAlternativeBilling
    );

    // console.log("Praxis Address", locationAddresses, addressId);

    if (
      locationAddresses.length &&
      locationAddresses.findIndex((add) => add.id == addressId) > -1
    ) {
      isSelectedAddressPraxisAddress = true;
    }
    return isSelectedAddressPraxisAddress;
  };

  const getAddressForAppointmentTemplate = (
    templateData: AppointmentTemplateDraft,
    rxs: RxDto[]
  ): string => {
    let addressId = "";
    try {
      if (templateData) templateData = { ...templateData };
      templateData.therapyRx.map((therapyRxObj) => {
        if (therapyRxObj.id && !templateData.addressId && !addressId) {
          const address = getAddressOfRxByTherapyRx(therapyRxObj.id, rxs);
          if (address?.id) addressId = address.id;
        }
      });
    } catch (error) {
      // console.log("Problem in assigning address");
    }

    return addressId;
  };
  const getDefaultVisitFeeByRxOrt = (
    therapyRxId: string,
    rxs: RxDto[],
    selectedTherapyData:TherapyRxDto[]
  ): string | null => {
    // Validate inputs
    if (!therapyRxId || !Array.isArray(selectedTherapyData) || selectedTherapyData.length === 0) {
      return null;
    }
  
    // Check if all therapyRxs are home visits
    const allAreHomeVisits = selectedTherapyData.every((therapyRx:any) =>
      therapyRx.rx.isHomeVisit === true
    );
  
    if (!allAreHomeVisits) {
      return null; // If any therapy prescription is not a home visit
    }
  
    // Extract the target address
    const targetAddress =
      addresses.find((address) => address.isVisit === true) || // Priority: isVisit === true
      addresses.find((address) => address.isPrimary === true); // Fallback: isPrimary === true
  
    if (!targetAddress) {
      return null; // No valid target address found
    }
  
    // Find the therapy prescription matching the therapyRxId
    const rxIndex = rxs.findIndex((rxObj) =>
      rxObj.therapyRxs?.some((thxObj) => thxObj.id === therapyRxId)
    );
  
    if (rxIndex === -1) {
      return null; // Therapy prescription not found
    }
  
    const rxVisitType = targetAddress?.type?.toLowerCase();
  
    // Determine the visit fee based on visit type and target address type
    if (rxVisitType ==="haus") {
      return "HB";
    } else if (
      rxVisitType === "heim"
    ) {
      return "HB2";
    }
  
    return null; // No valid match found
  };

  // const getDefaultVisitFeeByRxOrt = (thearpyRxId: string, rxs: RxDto[]): string | null => {


  //   // are all of the therapy rxs home vist?
  //   //       if not return null
  //   // extract target address 
  //   //     target address is the patients address with isVisit flag true, or if non are true, take the address with  isPrimary == true
  //   // ---> the type of this address tells you house vs heim ..
  //   let visitFee = null;

  //   const rxIndex = rxs.findIndex((rxObj) =>
  //     rxObj.therapyRxs?.some((thxobj) => thxobj.id == thearpyRxId)
  //   );

  //   if (rxIndex > -1) {
  //     let rxVisitType = rxs[rxIndex].visitType;

  //     //Haus
  //     if (rxVisitType == visitTypeReverseMapping["hb"]) {
  //       visitFee = "HB";
  //     } else if (rxVisitType == visitTypeReverseMapping["hm"]) {
  //       visitFee = "HB2";
  //     }
  //   }

  //   return visitFee;
  // };

  const resetSelectedVisitFeeObj = (therapRxId: string, isContinuous: boolean) => {
    updateCheckedMapVisitFeeObj(therapRxId, "", isContinuous);
    const setVisitFeeFunc = isContinuous ? setPerpetualSelectedVisitFeeObj : setSelectedVisitFeeObj;
    setVisitFeeFunc(undefined);
  };

  const assignVisitFeeToAppointmentTemplate = (
    templateData: AppointmentTemplateDraft,
    rxs: RxDto[],
    isContinuous: boolean,
    selectedTherapyData:TherapyRxDto[]
  ): AppointmentTemplateDraft => {
    if (templateData) templateData = { ...templateData };
    let visitFeeObj = isContinuous ? selectedPerpetualVisitFeeObj : selectedVisitFeeObj;
    let visitFee: string | null = visitFeeObj?.visitFee!;
    let firstTherapyRxIdAssgined = null;
    let setVisitFeeFunc = isContinuous ? setPerpetualSelectedVisitFeeObj : setSelectedVisitFeeObj;
    let visitFeeValueMapToFee: { [key: string]: string | null } = {};
    try {
  let visitFeeValue: string | null = null;

      templateData.therapyRx.map((therapyRxObj) => {
        if (therapyRxObj.id) {
          let defaultVisitFee = getDefaultVisitFeeByRxOrt(therapyRxObj.id, rxs,selectedTherapyData);

          //condition to handle ORT if Praxis where visitfee is null OR Impact in Visit Fee: If address is Praxis then remove visit fee from all therapyRxs
          if (!defaultVisitFee || visitFeeObj === null) {
            setLineVisitFee(templateData, therapyRxObj.id, null);
            return therapyRxObj;
          }

          //condition to assign the same visitFee to the other therapyRx as the first one in the templateData
          if (defaultVisitFee && !visitFee) visitFee = defaultVisitFee;

          // To make others null expect selected therapy

          if (
            visitFeeObj !== undefined &&
            visitFee &&
            visitFeeObj?.therapyRxId &&
            visitFeeObj.therapyRxId !== therapyRxObj.id
          ) {
            setLineVisitFee(templateData, therapyRxObj.id, null);
            //updateCheckedMapVisitFeeObj(therapyRxObj.id, "", isContinuous);
            return therapyRxObj;
          }

          if (visitFee && visitFeeValue) {
            setLineVisitFee(templateData, therapyRxObj.id, null);
            return therapyRxObj;
          }
          // condition when visitFee is assigned to the first therapyRx in the group in templateData
          if (visitFee) {
            // MD-232: tarif group comes from the Rx, not from the line. Read it from the
            // Rx itself: the line's `rx` stub from getRxLackingAppointments carries only
            // isHomeVisit and patient, so reading it there cleared every chosen fee.
            const parentRx: any = rxs.find((rxObj) =>
              rxObj.therapyRxs?.some((thxObj) => thxObj.id === therapyRxObj.id)
            );
            let trafiGroupIdSrc = parentRx?.tarifGroup?.id ?? parentRx?.tarifGroupId;
            if (trafiGroupIdSrc) {
              let trafiGroupId = trafiGroupIdSrc as string;
              visitFeeValue = getTherapyId(trafiGroupId as any, visitFee || "");
              setLineVisitFee(templateData, therapyRxObj.id, visitFeeValue!);
              firstTherapyRxIdAssgined = therapyRxObj.id;
              if (visitFeeValue) {
                setVisitFeeValueToFeeCodeMap((prevData) => ({
                  ...prevData,
                  [visitFeeValue!]: visitFee,
                }));
                visitFeeValueMapToFee[visitFeeValue] = visitFee;
              }

              // updateCheckedMapVisitFeeObj(therapyRxObj.id, visitFee, isContinuous);
            }
          }
        }
      });
    } catch (error) {
      // console.log("Problem in assigning address");
    }
    if (!visitFeeObj && firstTherapyRxIdAssgined && visitFee) {
      setVisitFeeFunc({ therapyRxId: firstTherapyRxIdAssgined, visitFee: visitFee });
    }

    return templateData;
  };

  const validateAppointmentTemplateOnSubmit = (
    templateData: AppointmentTemplateDraft,
    rxs: RxDto[],
    selectedTherapyData:TherapyRxDto[]
  ): string => {
    //TherpayRx length check
    if (!templateData.therapyRx.length) return "Bitte mindestens eine Zeile unter „Auswahl“ markieren.";

    //AddresId missing check
    if (!templateData.addressId) return "Bitte eine Adresse auswählen.";

    // Frequency was never checked here, so a template without one reached the
    // server and came back as a generic "Frequency is required" rejection.
    if (!templateData.frequency?.id) return "Bitte eine Frequenz auswählen.";

    //amount should be a number if amount property exists
    if (templateData?.hasOwnProperty("amount")) {
      if (typeof templateData.amount !== "number" || isNaN(templateData.amount)) {
        return "Bitte eine gültige Menge eingeben.";
      }
    }

    //Visit Fee not allowed if Praxis Address is selcted check
    if (isSelectedAddressIsPraxis(templateData.addressId)) {
      // console.log("Selected Address Praxis", templateData);

      if (hasAnyVisitFee(templateData))
        return "Hausbesuchsgebühr nicht erlaubt bei Praxis-Adresse.";
    } else {
      //Selected address is not praxis, check for visit fee
      let visitFeeApplied = false;
      let visitFeeCount = 0;
      for (let i = 0; i < templateData.therapyRx.length; i++) {
        if (lineVisitFee(templateData, templateData.therapyRx[i].id)) {
          visitFeeApplied = true;
          visitFeeCount = visitFeeCount + 1;
        }
      }
      if (!visitFeeApplied) return "Für Hausbesuche bitte eine HB Pauschale auswählen.";
      //there is visitFee; check for visitFee count shoudl not be greater than 1
      if (visitFeeCount > 1) return "Die HB Pauschale kann nur einmal vergeben werden.";
    }

    //If Praxis therapyRx is selected then address should be only praxis address

    for (let i = 0; i < templateData.therapyRx.length; i++) {
      if (!getDefaultVisitFeeByRxOrt(templateData.therapyRx[i].id!, rxs,selectedTherapyData)) {
        //Praxis is selected

        //Check if address is praxis or not
        if (!isSelectedAddressIsPraxis(templateData.addressId)) {
          //if address is not praxis
          return "Für Praxis-Rezepte ist nur die Praxis-Adresse möglich.";
        }
      }
      if (!(templateData.therapyRx[i]?.prescribedTherapyId ?? templateData.therapyRx[i]?.prescribedTherapy?.id)) {
        return "Für eine ausgewählte Zeile ist kein Heilmittel hinterlegt.";

      }
      if(!templateData.therapyRx[i].frequency){
        return "Für eine ausgewählte Zeile ist keine gültige Frequenz hinterlegt."
      }
    }

    return "";
  };

  /**
   * The draft/request boundary. `validateAppointmentTemplateOnSubmit` has
   * already rejected a missing frequency, so this only has to narrow the type —
   * it returns null rather than asserting, so a future validator change cannot
   * turn into a malformed request.
   */
  const asTemplateRequest = (
    draft: AppointmentTemplateDraft,
  ): AppointmentTemplateDto | null =>
    draft.frequency ? { ...draft, frequency: draft.frequency } : null;

  const handlecreateRx = async () => {
    const errorMessage = validateAppointmentTemplateOnSubmit(newData, notPerpetualRxs,selectedTherapies);
    if (errorMessage) {
      return notificationStore.showMessage(errorMessage, "error");
    }

    const request = asTemplateRequest(newData);
    if (!request) return notificationStore.showMessage("Bitte eine Frequenz auswählen.", "error");

    createForTreatments({ appointmentTemplateDto: request, isContinuous: false, patientId: patientId! });
    setAmount(0);
    setFrequency("");

    setCheckedMap({});
  };

  const handlecreateRxContinuous = async () => {
    const errorMessage = validateAppointmentTemplateOnSubmit(perpetualData, perpetaulRx,selectedPerpetualTherapies);
    if (errorMessage) {
      return notificationStore.showMessage(errorMessage, "error");
    }

    const request = asTemplateRequest(perpetualData);
    if (!request) return notificationStore.showMessage("Bitte eine Frequenz auswählen.", "error");

    createForTreatments({ appointmentTemplateDto: request, isContinuous: true, patientId: patientId! });
    setperpetualFrequency("");
  };

  const handleContinousRx = async () => {
    // setperpetualAmount(0);
    setperpetualFrequency(-1);
  };

  //   // This useEffect will run when the dialog box is opened.
  //   if (isSuggestionDialogOpen) {
  //     const fetchData = async () => {
  //       try {
  //         const response = await getSolutionsForContinuous(perpetualData, 4);
  //         console.log("response778", response);
  //         setSuggestionData(response);
  //       } catch (error) {
  //         console.log("error", error);
  //       }
  //     };

  //     fetchData();
  //   }
  // }, [isSuggestionDialogOpen]);

  const [checkedMap, setCheckedMap] = useState<{ [key: string]: string | null }>({});
  const [checkedMapPerpetual, setCheckedMapPerpetual] = useState<{ [key: string]: string | null }>(
    {}
  );

  const updateCheckedMapVisitFeeObj = (
    therapyRxId: string,
    visitFee: string,
    isContinuous: boolean
  ) => {
    if (isContinuous) {
      setCheckedMapPerpetual((prevState) => ({
        ...prevState,
        [therapyRxId]: visitFee,
      }));
    } else {
      setCheckedMap((prevState) => ({
        ...prevState,
        [therapyRxId]: visitFee,
      }));
    }
  };

  const handleDropDownMenuChange = (
    therapyId: any,
    newVisitFee: string,
    isContinuous: boolean
  ) => {
    const defaultVisitFee = getDefaultVisitFeeByRxOrt(
      therapyId,
      isContinuous ? perpetaulRx : notPerpetualRxs,
      isContinuous?selectedPerpetualTherapies:selectedTherapies
    );

    if (!defaultVisitFee) {
      updateCheckedMapVisitFeeObj(therapyId, "", isContinuous);
      return;
    }

    const appointmentTemplate: AppointmentTemplateDraft = isContinuous ? perpetualData : newData;
    const setVisitFeeFunc = isContinuous ? setPerpetualSelectedVisitFeeObj : setSelectedVisitFeeObj;
    const therapyExistsInSelectedGroup = appointmentTemplate.therapyRx.findIndex(
      (therapyRxObj) => therapyRxObj.id == therapyId
    );
    if (therapyExistsInSelectedGroup > -1) {
      //Triggers the assignment of visitFees to the group of therapyRx

      if (visitFeeOptions.findIndex((option) => option.value == newVisitFee) < 0) {
        setVisitFeeFunc(null);
        return;
      }

      setVisitFeeFunc((prevData) => ({
        ...prevData,
        therapyRxId: therapyId,
        visitFee: newVisitFee,
      }));
    } else {
      // updates the visitFee to single therapyRx
      updateCheckedMapVisitFeeObj(therapyId, newVisitFee, isContinuous);
    }
  };

  const getVisitFeeDropdownValue = (
    templateData: AppointmentTemplateDraft,
    therapyRxId: string,
    isContinuous: boolean
  ): string | undefined | null => {
    let selectedVisitFee = isContinuous ? selectedPerpetualVisitFeeObj : selectedVisitFeeObj;
    // let emptyValue = Math.random() * 1000 + "";
    let emptyValue = " ";
    //.repeat(Math.random() * 100);
    if (selectedVisitFee === null) {
      return emptyValue;
    }
    // let templateData = isContinuous ? perpetualData : newData;
    const thObjIndex = templateData.therapyRx?.findIndex((thObj) => thObj.id == therapyRxId);
    if (thObjIndex > -1) {
      let visitFeeValue = lineVisitFee(templateData, templateData.therapyRx?.[thObjIndex]?.id);

      if (visitFeeValue) {
        if (visitFeeValueToFeeCodeMap?.[visitFeeValue] == null) return emptyValue;
        return visitFeeValueToFeeCodeMap?.[visitFeeValue]!;

        // let visitFee = getVisitFeeFromVisitFeeValue(templateData, therapyRxId, visitFeeValue);
        // return visitFee;
      }
      return emptyValue;
    } else {
      //AddressId change is praxis address

      if (!checkedMap[therapyRxId]) return emptyValue;
      return checkedMap[therapyRxId] || emptyValue;
    }
  };
  // console.log("NewRx", newData, perpetualData, toJS(masterData), rxs, blockedData, patientId);
  const notPerpetualRx = rxnoAppointments?.filter((rx) => !rx.isPerpetual) || [];
  const perpetaulRx = rxnoAppointments?.filter((rx) => rx.isPerpetual) || [];
  // console.log("Not perpetual", notPerpetualRx);

  const visitFeeOptions = [
    { label: "HB2K", value: "HB2K" },
    { label: "HB7", value: "HB7" },
    { label: "HB", value: "HB" },
    { label: "HB2", value: "HB2" },
  ];

  // console.log("NewData", newData, perpetualData);
  // console.log("NewData", newData);
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

  return (
    <>
      <FlatCard title="Standardtermine">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {notPerpetualRx.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={6} sx={{ padding: "2px 200px" }}>
                  Keine ungeplante Rezepte
                </TableCell>
              </StyledTableRow>
            )}

            <StyledTableContainer>
              <StyledTable>
                {notPerpetualRx.length > 0 && (
                  <TableHead>
                    <StyledTableRow >
                      <StyledTableCell>Rezept</StyledTableCell>
                      <StyledTableCell>Heilm.</StyledTableCell>
                      <StyledTableCell>Freq.</StyledTableCell>
                      <StyledTableCell>Menge</StyledTableCell>
                      <StyledTableCell>Fehlt</StyledTableCell>
                      <StyledTableCell>Ort</StyledTableCell>
                      <StyledTableCell>HB Pauschale</StyledTableCell>
                      <StyledTableCell>Auswahl</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                )}
                <TableBody>
                  {notPerpetualRx.map((rx) => (
                    <React.Fragment key={rx.id}>
                      {rx?.therapyRxs
                        ?.filter(
                          (therapy) =>
                            // MD-232: a null amount means an open-ended line
                            // (BG Langzeit / Blanko plan line) — it always has
                            // room, so it is never filtered out here.
                            therapy?.amount == null ||
                            (therapy?.scheduledCount !== undefined &&
                              therapy.amount - therapy.scheduledCount !== 0)
                        ) // Filter based on scheduledCount
                        .sort((a: any, b: any) => a.order - b.order)
                        .map((therapy: any, index: any) => (
                          <StyledTableRow key={therapy.id}>
                            {index === 0 && (
                              <StyledTableCell
                                rowSpan={
                                  rx.therapyRxs?.filter(
                                    (therapy) =>
                                      therapy?.amount == null ||
                                      (therapy?.scheduledCount !== undefined &&
                                        therapy.amount - therapy.scheduledCount !== 0)
                                  ).length
                                }
                              >
                                {rx.rxNumber}
                              </StyledTableCell>
                            )}
                            <StyledTableCell>
                              {/* MD-325: `therapy.therapy` has not existed since
                                  MD-232 split prescribed from performed, so this
                                  cell was blank. Show the prescribed term, and
                                  offer the application choice when the term
                                  covers several. */}
                              <TherapyRxMethodCell
                                therapyRxId={therapy?.id}
                                fallbackLabel={
                                  therapy?.prescribedTherapy?.shortLabel ??
                                  therapy?.prescribedTherapy?.label ??
                                  therapy?.prescribedTherapyId
                                }
                              />
                            </StyledTableCell>
                            <StyledTableCell>{therapy?.frequency?.text}</StyledTableCell>
                            <StyledTableCell>{therapy?.amount}</StyledTableCell>
                            <StyledTableCell>
                              {therapy?.amount - therapy?.scheduledCount}
                            </StyledTableCell>
                            <StyledTableCell>{rx.isHomeVisit === true
                        ? "Haus/Heim"
                        : rx.isHomeVisit === false
                        ? "Praxis"
                        : "-"}</StyledTableCell>

                            <StyledTableCell>
                              <Box sx={{ minWidth: 110 }}>
                                <Controls.Select
                                  id="notPerpetualVisitFee"
                                  name="visitFEe"
                                  value={() => {
                                    let val = getVisitFeeDropdownValue(newData, therapy.id, false);
                                    return val;
                                  }}
                                  onChange={(e: any) => {
                                    handleDropDownMenuChange(
                                      therapy.id,
                                      e.target.value,
                                      false
                                    );
                                  }}
                                  options={
                                    visitFeeOptions
                                    // [
                                    //   { label: "HB2K", value: "HB2K" },
                                    //   { label: "HB7", value: "HB7" },
                                    //   { label: "HB", value: "HB" },
                                    // ]
                                  }
                                  disabled={!getDefaultVisitFeeByRxOrt(therapy.id, notPerpetualRxs,selectedTherapies)}
                                  style={{
                                    width: "100%",
                                    marginTop: "0px",
                                    marginBottom: "0px",
                                    fontSize: "10px",
                                  }}
                                />
                              </Box>
                            </StyledTableCell>

                            <StyledTableCell>
                              {selectedTherapies?.some((selected) => selected.id === therapy.id) ? (
                                <Button
                                  onClick={() => handleRemove(therapy.id)}
                                  sx={{
                                    backgroundColor: "success.main",
                                    color: "white",
                                    height: "20px",
                                    width: "15px",
                                    fontSize: "12px", // Adjust the font size as needed
                                  }}
                                >
                                  ✓
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => handleRowSelect(therapy.id)}
                                  sx={{
                                    backgroundColor: "error.main",
                                    color: "white",
                                    height: "20px",
                                    width: "15px",
                                    fontSize: "12px", // Adjust the font size as needed
                                  }}
                                >
                                  ✗
                                </Button>
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </Grid>
        </Grid>

        {notPerpetualRx.length !== 0 && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                type="number"
                label="Menge"
                variant="standard"
                sx={{ paddingLeft: "5px", height: "10px" }} // Adjust the width as needed
                // fullWidth
                value={amount}
                onChange={(e: any) => {
                  setAmount(Math.abs(parseInt(e.target.value)));
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Frequenz"
                variant="standard"
                sx={{ paddingLeft: "5px" }} // Adjust the width as needed
                // fullWidth
                value={frequency}
                onChange={(e: any) => setFrequency(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Address */}

              <FormControl
                variant="standard"
                sx={{ paddingLeft: "5px", minWidth: 60, width: "100%" }}
              >
                <InputLabel shrink>Address</InputLabel>
                <Select
                  value={(function () {
                    return addresses?.filter((add) => add.id == newData.addressId)[0]?.id || "";
                  })()}
                  onChange={(e: any) => {
                    setAddressId(e.target.value);
                    //onAddressChange(e.target.value, newData, notPerpetualRxs, false);
                  }}
                >
                  {addresses?.map((addressObj) => (
                    <MenuItem value={addressObj.id}>
                     {addressObj.type?visitTypeToIcon[addressObj.type?.toLowerCase()]:undefined} {addressObj.contactInfo?.addressLine1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handlecreateRx}
                sx={{ marginTop: "10px" }}
              >
                OK
              </Button>
            </Grid>
          </Grid>
        )}
      </FlatCard>
      <FlatCard title="Dauertermine">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {perpetaulRx.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={6} sx={{ padding: "2px 200px" }}>
                  Keine ungeplante Rezepte
                </TableCell>
              </StyledTableRow>
            )}

            <StyledTableContainer>
              <StyledTable>
                {perpetaulRx.length > 0 && (
                  <TableHead>
                    <StyledTableRow sx={{ backgroundColor: "white" }}>
                      <StyledTableCell>Rezept</StyledTableCell>
                      <StyledTableCell>Heilm.</StyledTableCell>
                      <StyledTableCell>Freq.</StyledTableCell>
                      <StyledTableCell>Menge</StyledTableCell>
                      <StyledTableCell>Fehlt</StyledTableCell>
                      <StyledTableCell>Ort</StyledTableCell>
                      <StyledTableCell>HB Pauschale</StyledTableCell>
                      <StyledTableCell>Auswahl</StyledTableCell>
                    </StyledTableRow>
                  </TableHead>
                )}
                <TableBody>
                  {perpetaulRx.map((rx) => (
                    <React.Fragment key={rx.id}>
                      {rx?.therapyRxs
                        ?.filter(
                          (therapy) =>
                            // MD-232: a null amount means an open-ended line
                            // (BG Langzeit / Blanko plan line) — it always has
                            // room, so it is never filtered out here.
                            therapy?.amount == null ||
                            (therapy?.scheduledCount !== undefined &&
                              therapy.amount - therapy.scheduledCount !== 0)
                        ) // Filter based on scheduledCount
                        .map((therapy: any, index: any) => (
                          <StyledTableRow key={therapy.id}>
                            {index === 0 && (
                              <StyledTableCell
                                rowSpan={
                                  rx.therapyRxs?.filter(
                                    (therapy) =>
                                      therapy?.amount == null ||
                                      (therapy?.scheduledCount !== undefined &&
                                        therapy.amount - therapy.scheduledCount !== 0)
                                  ).length
                                }
                              >
                                {rx.rxNumber}
                              </StyledTableCell>
                            )}
                            <StyledTableCell>
                              {/* MD-325: `therapy.therapy` has not existed since
                                  MD-232 split prescribed from performed, so this
                                  cell was blank. Show the prescribed term, and
                                  offer the application choice when the term
                                  covers several. */}
                              <TherapyRxMethodCell
                                therapyRxId={therapy?.id}
                                fallbackLabel={
                                  therapy?.prescribedTherapy?.shortLabel ??
                                  therapy?.prescribedTherapy?.label ??
                                  therapy?.prescribedTherapyId
                                }
                              />
                            </StyledTableCell>
                            <StyledTableCell>{therapy?.frequency?.text}</StyledTableCell>
                            <StyledTableCell>{therapy?.amount}</StyledTableCell>
                            <StyledTableCell>
                              {therapy?.amount - therapy?.scheduledCount}
                            </StyledTableCell>
                            <StyledTableCell>{rx.isHomeVisit === true
                        ? "Haus/Heim"
                        : rx.isHomeVisit === false
                        ? "Praxis"
                        : "-"}</StyledTableCell>
                            <StyledTableCell>
                              {" "}
                              {/* The pull down in Dauertermine for the visit fee */}
                              <Box sx={{ minWidth: 110 }}>
                                <Controls.Select
                                  id="perpetualVisitFee"
                                  name="visitFEePerpetual"
                                  value={() => {
                                    let val = getVisitFeeDropdownValue(
                                      perpetualData,
                                      therapy.id,
                                      true
                                    );
                                    return val;
                                  }}
                                  onChange={(e: any) =>
                                    handleDropDownMenuChange(
                                      therapy.id,
                                      e.target.value,
                                      true
                                    )
                                  }
                                  options={
                                    visitFeeOptions
                                    // [
                                    //   { label: "HB2K", value: "HB2K" },
                                    //   { label: "HB7", value: "HB7" },
                                    //   { label: "HB", value: "HB" },
                                    // ]
                                  }
                                  disabled={!getDefaultVisitFeeByRxOrt(therapy.id, perpetaulRx,selectedPerpetualTherapies)}
                                  // disabled={readOnlyView}
                                  style={{
                                    width: "100%",
                                    marginTop: "0px",
                                    marginBottom: "0px",
                                    fontSize: "10px",
                                  }}
                                />
                              </Box>
                            </StyledTableCell>
                            <StyledTableCell>
                              {selectedPerpetualTherapies?.some(
                                (selected) => selected.id === therapy.id
                              ) ? (
                                <Button
                                  onClick={() => handleperpetualRemove(therapy.id)}
                                  sx={{
                                    backgroundColor: "success.main",
                                    color: "white",
                                    height: "20px",
                                    width: "15px",
                                    fontSize: "12px", // Adjust the font size as needed
                                  }}
                                >
                                  ✓
                                </Button>
                              ) : (
                                <Button
                                  variant="outlined"
                                  onClick={() => handleperpetualRowSelect(therapy.id)}
                                  sx={{
                                    backgroundColor: "error.main",
                                    color: "white",
                                    height: "20px",
                                    width: "15px",
                                    fontSize: "12px", // Adjust the font size as needed
                                  }}
                                >
                                  ✗
                                </Button>
                              )}
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                    </React.Fragment>
                  ))}
                </TableBody>
              </StyledTable>
            </StyledTableContainer>
          </Grid>
        </Grid>
        {perpetaulRx.length !== 0 && (
          <Grid container spacing={2}>
            {/* <Grid item xs={12} md={4}>
  <TextField
      label="Amount"
      variant="standard"
      sx={{ paddingLeft: '5px',height:"10px" }} // Adjust the width as needed
      // fullWidth
      value={perpetualAmount}
      onChange={(e: any) => setperpetualAmount(e.target.value)}
      InputLabelProps={{ shrink: true }}
    />
  </Grid> */}
            <Grid item xs={12} md={3}>
              <TextField
                label="Frequency"
                variant="standard"
                sx={{ paddingLeft: "5px" }} // Adjust the width as needed
                // fullWidth
                value={perpetualfrequency}
                onChange={(e: any) => setperpetualFrequency(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={5}>
              {/* Address */}

              <FormControl
                variant="standard"
                sx={{ paddingLeft: "5px", minWidth: 60, width: "100%" }}
              >
                <InputLabel shrink>Address</InputLabel>
                <Select
                  value={(function () {
                    return (
                      addresses?.filter((add) => add.id == perpetualData.addressId)[0]?.id || ""
                    );
                  })()}
                  onChange={(e: any) => {
                    setPerpetualAddressId(e.target.value);
                  }}
                >
                  {addresses?.map((addressObj) => (
                    <MenuItem value={addressObj.id}>
                      {addressObj.type?visitTypeToIcon[addressObj.type?.toLowerCase()]:undefined}{addressObj.contactInfo?.addressLine1}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              {/* <Button
      variant="contained"
      color="primary"
      onClick={() => handleContinousRx()}
      sx={{marginTop:"10px"}}
    >
      OK
    </Button> */}
              <Button
                variant="contained"
                color="primary"
                // onClick={openSuggestionDialog}
                onClick={handlecreateRxContinuous}
                sx={{ marginTop: "10px" }}
              >
                OK
              </Button>
            </Grid>
            {/* <AppointmentSuggestion
            open={isSuggestionDialogOpen}
            onClose={() => setsuggestionDialogOpen(false)}
            continousData={perpetualData}
            setDataRefreshFlag={setDataRefreshFlag}
            dataRefreshFlag={dataRefreshFlag}
            patientId={patientId}
            blockedData={blockedData}
            setblockedData={setblockedData}
            continous={true}
            handleContinousRx={handleContinousRx}
            patientAvailabilityData={patientAvailablityData}
            setPatientAvailabilityData={setPatientAvailablityData}
          /> */}
          </Grid>
        )}
      </FlatCard>
    </>
  );
});

export default NewRx;
